/**
 * Routes pointer events to the right pane's controls in multi-view mode.
 *
 * Approach: only ONE pane's controls is registered with viewer.inputHandler at
 * any time — the pane the cursor is currently over. When the cursor crosses
 * pane boundaries, we swap the registered listener. During an active drag we
 * keep the original listener (drag continuity), so dragging out of a pane and
 * back in still rotates/pans the pane the drag started in.
 *
 * This approach avoids modifying the existing InputHandler or OrbitControls
 * classes — it only manipulates `inputHandler.addInputListener / removeInputListener`.
 */

export class MultiViewInputHandler {
  /**
   * @param {object} viewer
   * @param {import('./MultiViewLayout.js').MultiViewLayout} layout
   */
  constructor(viewer, layout) {
    this.viewer = viewer;
    this.layout = layout;
    this.domElement = viewer.renderer.domElement;
    this.activePane = null;

    // Suspend the single-view controls so they don't also react to drags
    // (they'd push deltas into scene.view, which the multi-view render path
    // ignores anyway — but they're noisy and slow). Restored on dispose().
    this._suspendedControls = null;
    if (viewer.controls) {
      this._suspendedControls = viewer.controls;
      viewer.inputHandler.removeInputListener(viewer.controls);
      viewer.controls.enabled = false;
    }

    this._onMouseMove = this._onMouseMove.bind(this);
    this._onMouseDown = this._onMouseDown.bind(this);
    this._onMouseLeave = this._onMouseLeave.bind(this);
    this._onWheel = this._onWheel.bind(this);

    this.domElement.addEventListener('mousemove', this._onMouseMove);
    this.domElement.addEventListener('mousedown', this._onMouseDown);
    this.domElement.addEventListener('mouseleave', this._onMouseLeave);
    // Wheel events do not trigger mousemove, so the activePane derived from the
    // last cursor-move may not reflect the pane the user is actually scrolling
    // over (e.g. immediately after page load, or after a programmatic focus
    // shift). Capture-phase listeners on both modern ('wheel') and legacy
    // ('mousewheel') names ensure we always swap the active pane before
    // Potree's InputHandler dispatches the wheel to listeners.
    this.domElement.addEventListener('wheel', this._onWheel, true);
    this.domElement.addEventListener('mousewheel', this._onWheel, true);
  }

  /** Translate a DOM mouse event into canvas-local WebGL coordinates (origin bottom-left). */
  _glCoordsFromEvent(e) {
    const rect = this.domElement.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const yTopOrigin = e.clientY - rect.top;
    // Flip Y: WebGL viewport coordinates have origin at bottom-left.
    return { x, y: rect.height - yTopOrigin, canvasW: rect.width, canvasH: rect.height };
  }

  _isDragging() {
    return !!this.viewer.inputHandler && !!this.viewer.inputHandler.drag;
  }

  _setActivePane(pane) {
    if (pane === this.activePane) return;
    if (this.activePane && this.activePane.controls) {
      this.viewer.inputHandler.removeInputListener(this.activePane.controls);
    }
    this.activePane = pane;
    if (pane && pane.controls) {
      this.viewer.inputHandler.addInputListener(pane.controls);
    }
  }

  _onMouseMove(e) {
    if (this._isDragging()) return; // don't swap mid-drag
    const c = this._glCoordsFromEvent(e);
    const pane = this.layout.paneAtPixel(c.x, c.y, c.canvasW, c.canvasH);
    this._setActivePane(pane);
  }

  _onMouseDown(e) {
    // Make sure the correct pane is active before InputHandler synthesizes a drag.
    const c = this._glCoordsFromEvent(e);
    const pane = this.layout.paneAtPixel(c.x, c.y, c.canvasW, c.canvasH);
    this._setActivePane(pane);
  }

  _onMouseLeave() {
    if (this._isDragging()) return;
    this._setActivePane(null);
  }

  /**
   * Take full responsibility for wheel events while multi-view is active.
   *
   * Why: Potree's InputHandler listens to `'mousewheel'` (legacy WebKit/IE
   * name) and `'DOMMouseScroll'` (Firefox legacy). Chrome 125+ deprecated
   * `'mousewheel'`; some Chrome variants no longer fire it at all and only
   * fire the modern `'wheel'` event. In that case `InputHandler.onMouseWheel`
   * never runs and the pane never receives a `mousewheel`-type event, so
   * wheel zoom silently breaks. We can't fix that on InputHandler without
   * regressing the single-view path, so the multi-view handler does the
   * dispatch itself.
   *
   * Strategy:
   *   1. Locate the pane under the cursor and make it active.
   *   2. Normalise the wheel delta (handles WheelEvent.deltaY, legacy
   *      wheelDelta, and Firefox detail).
   *   3. Dispatch `{type: 'mousewheel', delta: ±1}` straight to that pane's
   *      OrbitControls — same shape Potree's InputHandler would have used.
   *   4. preventDefault + stopImmediatePropagation so the browser doesn't
   *      page-scroll AND so Potree's bubble-phase InputHandler doesn't
   *      double-dispatch when both 'wheel' and 'mousewheel' fire.
   */
  _onWheel(e) {
    const c = this._glCoordsFromEvent(e);
    const pane = this.layout.paneAtPixel(c.x, c.y, c.canvasW, c.canvasH);
    if (!pane) return;
    this._setActivePane(pane);

    let delta = 0;
    if (e.wheelDelta !== undefined && e.wheelDelta !== 0) {
      delta = e.wheelDelta;
    } else if (e.deltaY !== undefined && e.deltaY !== 0) {
      // WheelEvent.deltaY is positive when scrolling down (away from user).
      // Potree's convention is the opposite, so flip the sign.
      delta = -e.deltaY;
    } else if (e.detail !== undefined && e.detail !== 0) {
      delta = -e.detail;
    }
    if (delta === 0) return;
    const ndelta = Math.sign(delta);

    if (pane.controls && typeof pane.controls.dispatchEvent === 'function') {
      pane.controls.dispatchEvent({ type: 'mousewheel', delta: ndelta, object: null });
    }
    if (e.cancelable) e.preventDefault();
    e.stopImmediatePropagation();
  }

  /**
   * Drive each pane's controls forward in time. Called from the viewer's
   * update loop. Only the active pane's controls produce visible motion (the
   * others have zero accumulated deltas), but advancing them all keeps any
   * residual deceleration alive after the cursor leaves a pane.
   */
  updateAll(delta) {
    for (const pane of this.layout.panes) {
      if (pane.controls && typeof pane.controls.update === 'function') {
        pane.controls.update(delta);
      }
    }
  }

  dispose() {
    this.domElement.removeEventListener('mousemove', this._onMouseMove);
    this.domElement.removeEventListener('mousedown', this._onMouseDown);
    this.domElement.removeEventListener('mouseleave', this._onMouseLeave);
    this.domElement.removeEventListener('wheel', this._onWheel, true);
    this.domElement.removeEventListener('mousewheel', this._onWheel, true);
    if (this.activePane && this.activePane.controls) {
      this.viewer.inputHandler.removeInputListener(this.activePane.controls);
    }
    this.activePane = null;
    // Restore the single-view controls so the regular CloudViewer behaviour
    // resumes if the same Viewer instance is reused after teardown.
    if (this._suspendedControls) {
      this._suspendedControls.enabled = true;
      this.viewer.inputHandler.addInputListener(this._suspendedControls);
      this._suspendedControls = null;
    }
  }
}
